import type { HTMLAttributes, Ref } from 'react'
import { cx } from '@/shared/lib'
import s from './Spinner.module.css'

/** Ступени шкалы: xs — внутри контрола, sm — строка загрузки, md — центр блока, lg — центр экрана. */
export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg'

/**
 * `onAccent` — на цветной или тёмной заливке: дорожка и дуга белые.
 * `current` — дуга берёт цвет текста контейнера: спиннер внутри кнопки обязан совпадать
 * с её подписью во всех вариантах, тонах и обеих темах, а перечислять их у себя он не может.
 */
export type SpinnerTone = 'default' | 'onAccent' | 'current'

export type SpinnerProps = {
  size?: SpinnerSize
  tone?: SpinnerTone
  /**
   * Подпись для ассистивных технологий.
   * Задана — спиннер становится живой областью `role="status"` и объявляет загрузку.
   * Не задана — спиннер декоративный (`aria-hidden`): значит, о загрузке сообщает родитель
   * (кнопка с `aria-busy`, чип с видимым текстом, строка `role="status"`).
   */
  label?: string
  ref?: Ref<HTMLSpanElement>
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const TONE_CLASS: Record<SpinnerTone, string | undefined> = {
  default: undefined,
  onAccent: s.onAccent,
  current: s.current,
}

/** Индикатор неопределённой загрузки. Для операций короче секунды; дольше — Skeleton. */
export function Spinner({
  size = 'sm',
  tone = 'default',
  label,
  className,
  ref,
  ...rest
}: SpinnerProps) {
  const announced = Boolean(label)

  return (
    <span
      {...rest}
      ref={ref}
      className={cx(s.root, s[size], TONE_CLASS[tone], className)}
      role={announced ? 'status' : undefined}
      aria-hidden={announced ? undefined : true}
    >
      {announced ? <span className={s.label}>{label}</span> : null}
    </span>
  )
}
